import { msg } from "@lingui/core/macro";
import { graphql } from "gql.tada";

// #region SearchAnalyticsSearchType
export type SearchAnalyticsSearchType = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsSearchType">
>;
const SearchAnalyticsSearchTypes = [
  "WEB",
  "IMAGE",
  "VIDEO",
  "NEWS",
  "DISCOVER",
  "GOOGLE_NEWS",
] as const satisfies SearchAnalyticsSearchType[];
export const SearchAnalyticsSearchTypeLabels: Record<
  SearchAnalyticsSearchType,
  ReturnType<typeof msg>
> = {
  WEB: msg`Web`,
  IMAGE: msg`Image`,
  VIDEO: msg`Video`,
  NEWS: msg`News`,
  DISCOVER: msg`Discover`,
  GOOGLE_NEWS: msg`Google News`,
} as const;
export const SearchAnalyticsSearchTypeOptions = SearchAnalyticsSearchTypes.map(
  (type) => ({
    value: type,
    label: SearchAnalyticsSearchTypeLabels[type],
  }),
);
export function isSearchAnalyticsSearchType(
  value: unknown,
): value is SearchAnalyticsSearchType {
  return SearchAnalyticsSearchTypes.includes(
    value as SearchAnalyticsSearchType,
  );
}
// #endregion

// #region SearchAnalyticsFilter
export type SearchAnalyticsFilter = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsFilter">
>;
const SearchAnalyticsFilters = [
  "CONTAINS",
  "NOT_CONTAINS",
  "EQUALS",
  "STARTS_WITH",
  "ENDS_WITH",
  "REGEX",
  "NOT_REGEX",
  "INCLUDE", // 非推奨
] as const satisfies SearchAnalyticsFilter[];
export function isSearchAnalyticsFilter(
  value: unknown,
): value is SearchAnalyticsFilter {
  return SearchAnalyticsFilters.includes(value as SearchAnalyticsFilter);
}
export const SearchAnalyticsFilterLabels: Record<
  SearchAnalyticsFilter,
  ReturnType<typeof msg>
> = {
  CONTAINS: msg`Contains`,
  NOT_CONTAINS: msg`Not contains`,
  EQUALS: msg`Equals`,
  STARTS_WITH: msg`Starts with`,
  ENDS_WITH: msg`Ends with`,
  REGEX: msg`Regex`,
  NOT_REGEX: msg`Not regex`,
  INCLUDE: msg`Contains`, // 非推奨
} as const;
export const SearchAnalyticsFilterOptions = SearchAnalyticsFilters.filter(
  (filter) => filter !== "INCLUDE", // 非推奨
).map((filter) => ({
  value: filter,
  label: SearchAnalyticsFilterLabels[filter],
}));
// #endregion

// #region SearchAnalyticsOrderField
export type SearchAnalyticsOrderField = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsOrderField">
>;
export const SearchAnalyticsOrderFields = [
  "DATE",
  "WEEK",
  "MONTH",
  "CLICKS",
  "IMPRESSIONS",
  "CTR",
  "AVERAGE_OF_RANK",
  "DISTINCT_QUERIES",
  "DISTINCT_URLS",
  "QUERY_LENGTH",
] as const satisfies SearchAnalyticsOrderField[];
export function isSearchAnalyticsOrderField(
  value: unknown,
): value is SearchAnalyticsOrderField {
  return SearchAnalyticsOrderFields.includes(
    value as SearchAnalyticsOrderField,
  );
}
export const SearchAnalyticsOrderFieldLabels: Record<
  SearchAnalyticsOrderField,
  ReturnType<typeof msg>
> = {
  DATE: msg`Date`,
  WEEK: msg`Week`,
  MONTH: msg`Month`,
  CLICKS: msg`Clicks`,
  IMPRESSIONS: msg`Impressions`,
  CTR: msg`CTR`,
  AVERAGE_OF_RANK: msg`Avg. position`,
  DISTINCT_QUERIES: msg`Unique queries`,
  DISTINCT_URLS: msg`Unique URLs`,
  QUERY_LENGTH: msg`Query length`,
} as const;
export const SearchAnalyticsOrderFieldOptions = SearchAnalyticsOrderFields.map(
  (field) => ({
    value: field,
    label: SearchAnalyticsOrderFieldLabels[field],
  }),
);
// #endregion

// #region SearchAnalyticsComparingOrderField
export type SearchAnalyticsComparingOrderField = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsComparingOrderField">
>;
export const SearchAnalyticsComparingOrderFields = [
  "CLICKS",
  "CLICKS_DIFFERENCE",
  "IMPRESSIONS",
  "IMPRESSIONS_DIFFERENCE",
  "CTR",
  "CTR_DIFFERENCE",
  "AVERAGE_OF_RANK",
  "AVERAGE_OF_RANK_DIFFERENCE",
  "DISTINCT_URLS",
  "DISTINCT_URLS_DIFFERENCE",
  "DISTINCT_QUERIES",
  "DISTINCT_QUERIES_DIFFERENCE",
] as const satisfies SearchAnalyticsComparingOrderField[];
export function isSearchAnalyticsComparingOrderField(
  value: unknown,
): value is SearchAnalyticsComparingOrderField {
  return SearchAnalyticsComparingOrderFields.includes(
    value as SearchAnalyticsComparingOrderField,
  );
}
export const SearchAnalyticsComparingOrderFieldLabels: Record<
  SearchAnalyticsComparingOrderField,
  ReturnType<typeof msg> // string から ReturnType<typeof msg> に変更
> = {
  CLICKS: msg`Clicks`,
  CLICKS_DIFFERENCE: msg`Clicks difference`,
  IMPRESSIONS: msg`Impressions`,
  IMPRESSIONS_DIFFERENCE: msg`Impressions difference`,
  CTR: msg`CTR`,
  CTR_DIFFERENCE: msg`CTR difference`,
  AVERAGE_OF_RANK: msg`Avg. position`,
  AVERAGE_OF_RANK_DIFFERENCE: msg`Avg. position difference`,
  DISTINCT_QUERIES: msg`Unique queries`,
  DISTINCT_QUERIES_DIFFERENCE: msg`Unique queries difference`,
  DISTINCT_URLS: msg`Unique URLs`,
  DISTINCT_URLS_DIFFERENCE: msg`Unique URLs difference`,
};
export const SearchAnalyticsComparingOrderFieldOptions =
  SearchAnalyticsComparingOrderFields.map((field) => ({
    value: field,
    label: SearchAnalyticsComparingOrderFieldLabels[field],
  }));
// #endregion

// #region OrderDirection
export type OrderDirection = ReturnType<
  typeof graphql.scalar<"OrderDirection">
>;
export const OrderDirections = [
  "ASC",
  "DESC",
] as const satisfies OrderDirection[];
export function isOrderDirection(value: unknown): value is OrderDirection {
  return OrderDirections.includes(value as OrderDirection);
}
export const OrderDirectionLabels: Record<
  OrderDirection,
  ReturnType<typeof msg>
> = {
  ASC: msg`Ascending`,
  DESC: msg`Descending`,
} as const;
// #endregion

// #region SearchAnalyticsGroupBy
export type SearchAnalyticsGroupBy = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsGroupBy">
>;
const SearchAnalyticsGroupBys = [
  "QUERY_GROUP",
  "URL_GROUP",
] as const satisfies SearchAnalyticsGroupBy[];
const SearchAnalyticsGroupByLabels: Record<
  SearchAnalyticsGroupBy,
  ReturnType<typeof msg>
> = {
  QUERY_GROUP: msg`Query group`,
  URL_GROUP: msg`URL group`,
} as const;
const _SearchAnalyticsGroupByOptions = SearchAnalyticsGroupBys.map(
  (groupBy) => ({
    value: groupBy,
    label: SearchAnalyticsGroupByLabels[groupBy],
  }),
);
// #endregion

// #region SearchAnalyticsRawDataGroupBy
export type SearchAnalyticsRawDataGroupBy = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsRawDataGroupBy">
>;
const SearchAnalyticsRawDataGroupBys = [
  "DATE",
  "WEEK",
  "MONTH",
  "QUERY",
  "QUERY_GROUP",
  "URL",
  "URL_GROUP",
  "SEARCH_TYPE",
  "DEVICE",
  "COUNTRY",
  "IS_ANONYMIZED_QUERY",
  "IS_ANONYMIZED_DISCOVER",
  "RAW_KIND",
] as const satisfies SearchAnalyticsRawDataGroupBy[];
const SearchAnalyticsRawDataGroupByLabels: Record<
  SearchAnalyticsRawDataGroupBy,
  ReturnType<typeof msg>
> = {
  DATE: msg`Date`,
  WEEK: msg`Week`,
  MONTH: msg`Month`,
  QUERY: msg`Query`,
  QUERY_GROUP: msg`Query group`,
  URL: msg`URL`,
  URL_GROUP: msg`URL group`,
  SEARCH_TYPE: msg`Search type`,
  DEVICE: msg`Device`,
  COUNTRY: msg`Country`,
  IS_ANONYMIZED_QUERY: msg`Is anonymized query`,
  IS_ANONYMIZED_DISCOVER: msg`Is anonymized Discover`,
  RAW_KIND: msg`Search Appearances`,
} as const;
export const SearchAnalyticsRawDataGroupByOptions =
  SearchAnalyticsRawDataGroupBys.map((groupBy) => ({
    value: groupBy,
    label: SearchAnalyticsRawDataGroupByLabels[groupBy],
  }));
// #endregion

// #region SearchAnalyticsDataDateType
export type SearchAnalyticsDataDateType = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsDataDateType">
>;
const _SearchAnalyticsDataDateTypes = [
  "LATEST",
  "DAYS_7",
  "DAYS_14",
  "DAYS_28",
  "DAYS_90",
  "DAYS_180",
  "DAYS_365",
  "DAYS_480",
  "MONTHS_3",
  "MONTHS_6",
  "MONTHS_12",
  "MONTHS_16",
  "CUSTOM",
] as const satisfies SearchAnalyticsDataDateType[];
export const SearchAnalyticsDataDateTypeLabels: Record<
  SearchAnalyticsDataDateType,
  ReturnType<typeof msg>
> = {
  LATEST: msg`Latest`,
  DAYS_7: msg`Past 7 days`,
  DAYS_14: msg`Past 14 days`,
  DAYS_28: msg`Past 28 days`,
  DAYS_90: msg`Past 90 days`,
  DAYS_180: msg`Past 180 days`,
  DAYS_365: msg`Past 365 days`,
  DAYS_480: msg`Past 480 days`,
  MONTHS_3: msg`Past 3 months`,
  MONTHS_6: msg`Past 6 months`,
  MONTHS_12: msg`Past 12 months`,
  MONTHS_16: msg`Past 16 months`,
  CUSTOM: msg`Custom`,
} as const;
// #endregion

// #region SaDateOptionSingle
export const SaDateOptionSingles = [
  "latest",
  "7d",
  "14d",
  "28d",
  "3m",
  "custom",
  "saved",
] as const;
export type SaDateOptionSingle = (typeof SaDateOptionSingles)[number];
export function isSaDateOptionSingle(
  value: unknown,
): value is SaDateOptionSingle {
  return SaDateOptionSingles.includes(value as SaDateOptionSingle);
}
export const SaDateOptionSingleLabels: Record<
  SaDateOptionSingle,
  ReturnType<typeof msg>
> = {
  latest: msg`Latest date`,
  "7d": msg`Past 7 days`,
  "14d": msg`Past 14 days`,
  "28d": msg`Past 28 days`,
  "3m": msg`Past 90 days`,
  custom: msg`Custom`,
  saved: msg`Saved date`,
} as const;
export const SaDateOptionSingleOptions = SaDateOptionSingles.map((option) => ({
  value: option,
  label: SaDateOptionSingleLabels[option],
}));
// #endregion

// #region SaDateOptionComparison
export const SaDateOptionComparisons = [
  "comp_7d",
  "comp_14d",
  "comp_28d",
  "comp_3m",
  "comp_this_month",
  "comp_last_month",
  "comp_custom",
] as const;
export type SaDateOptionComparison = (typeof SaDateOptionComparisons)[number];
export function isSaDateOptionComparison(
  value: unknown,
): value is SaDateOptionComparison {
  return SaDateOptionComparisons.includes(value as SaDateOptionComparison);
}
export const SaDateOptionComparisonLabels: Record<
  SaDateOptionComparison,
  ReturnType<typeof msg>
> = {
  comp_7d: msg`Past 7 days vs previous 7 days`,
  comp_14d: msg`Past 14 days vs previous 14 days`,
  comp_28d: msg`Past 28 days vs previous 28 days`,
  comp_3m: msg`Past 90 days vs previous 90 days`,
  comp_this_month: msg`Current month vs same month last year`,
  comp_last_month: msg`Previous month vs same month last year`,
  comp_custom: msg`Custom Comparison`,
} as const;

export const SaDateOptionComparisonOptions = SaDateOptionComparisons.map(
  (option) => ({
    value: option,
    label: SaDateOptionComparisonLabels[option],
  }),
);
// #endregion

// #region DateOption
export type SaDateOption = SaDateOptionSingle | SaDateOptionComparison;
function _isSaDateOption(value: unknown): value is SaDateOption {
  return isSaDateOptionSingle(value) || isSaDateOptionComparison(value);
}
// #endregion

// #region SearchAnalyticsRawDataKind
const SearchAnalyticsRawDataKinds = [
  "JOB_LISTING",
  "JOB_DETAILS",
  "RECIPE_FEATURE",
  "RECIPE_RICH_SNIPPET",
  "REVIEW_SNIPPET",
  "VIDEO",
  "TPF_QA",
  "TPF_FAQ",
  "MERCHANT_LISTINGS",
  "PRODUCT_SNIPPETS",
  "FORUMS",
] as const satisfies ReturnType<
  typeof graphql.scalar<"SearchAnalyticsRawDataKind">
>[];
export type SearchAnalyticsRawDataKind =
  (typeof SearchAnalyticsRawDataKinds)[number];
export const SearchAnalyticsRawDataKindLabels: Record<
  SearchAnalyticsRawDataKind,
  ReturnType<typeof msg> // string から ReturnType<typeof msg> に変更
> = {
  JOB_LISTING: msg`Job listing`,
  JOB_DETAILS: msg`Job details`,
  RECIPE_FEATURE: msg`Recipe galleries`,
  RECIPE_RICH_SNIPPET: msg`Recipe rich results`,
  REVIEW_SNIPPET: msg`Review snippet`,
  VIDEO: msg`Videos`,
  TPF_QA: msg`Q&A rich results`,
  TPF_FAQ: msg`FAQ rich results`,
  MERCHANT_LISTINGS: msg`Merchant listings`,
  PRODUCT_SNIPPETS: msg`Product snippets`,
  FORUMS: msg`Discussions and forums`,
} as const;
export const SearchAnalyticsRawDataKindOptions =
  SearchAnalyticsRawDataKinds.map((kind) => ({
    value: kind,
    label: SearchAnalyticsRawDataKindLabels[kind],
  }));
export function isSearchAnalyticsRawDataKind(
  value: unknown,
): value is SearchAnalyticsRawDataKind {
  return SearchAnalyticsRawDataKinds.includes(
    value as SearchAnalyticsRawDataKind,
  );
}
// #endregion

// #region SaFilterState
export type SaFilterState = (
  | { field: "queryGroupIds"; value: string[] }
  | { field: "query"; value: { type: SearchAnalyticsFilter; pattern: string } }
  | { field: "urlGroupIds"; value: string[] }
  | { field: "url"; value: { type: SearchAnalyticsFilter; pattern: string } }
  | { field: "searchTypes"; value: SearchAnalyticsSearchType[] }
  | { field: "devices"; value: Device[] }
  | { field: "countries"; value: string[] }
  | { field: "rawkinds"; value: SearchAnalyticsRawDataKind[] }
  | { field: "rawkindsNot"; value: SearchAnalyticsRawDataKind[] }
  | { field: "isAnonymousQuery"; value: boolean | undefined }
)[];
export const SaArrayFilterFields = [
  "queryGroupIds",
  "urlGroupIds",
  "searchTypes",
  "devices",
  "countries",
  "rawkinds",
  "rawkindsNot",
] as const;
export type SaArrayFilterField = (typeof SaArrayFilterFields)[number];
export function isSaArrayFilterField(
  value: unknown,
): value is SaArrayFilterField {
  return SaArrayFilterFields.includes(value as SaArrayFilterField);
}
export const SaObjectFilterFields = ["query", "url"] as const;
export type SaObjectFilterField = (typeof SaObjectFilterFields)[number];
export function isSaObjectFilterField(
  value: unknown,
): value is SaObjectFilterField {
  return SaObjectFilterFields.includes(value as SaObjectFilterField);
}

export const SaBooleanFilterFields = ["isAnonymousQuery"] as const;
export type SaBooleanFilterField = (typeof SaBooleanFilterFields)[number];
export function isSaBooleanFilterField(
  value: unknown,
): value is SaBooleanFilterField {
  return SaBooleanFilterFields.includes(value as SaBooleanFilterField);
}

export type SaFilterField =
  | SaArrayFilterField
  | SaObjectFilterField
  | SaBooleanFilterField;

export const SaFilterFieldLabels: Record<
  SaFilterField,
  ReturnType<typeof msg>
> = {
  queryGroupIds: msg`Query group`,
  query: msg`Query`,
  urlGroupIds: msg`URL group`,
  url: msg`URL`,
  searchTypes: msg`Search type`,
  devices: msg`Device`,
  countries: msg`Country`,
  rawkinds: msg`Search Appearances`,
  rawkindsNot: msg`Search Appearances to exclude`,
  isAnonymousQuery: msg`Anonymous query`,
} as const;
// #endregion

// #region AnonymousQueryOption
const AnonymousQueryOptionValues = [
  "all",
  "nonAnonymousOnly",
  "anonymousOnly",
] as const;
export type AnonymousQueryOptionValue =
  (typeof AnonymousQueryOptionValues)[number];
export const AnonymousQueryOptionLabels: Record<
  AnonymousQueryOptionValue,
  ReturnType<typeof msg>
> = {
  all: msg`All queries`,
  anonymousOnly: msg`Anonymous queries only`,
  nonAnonymousOnly: msg`Non-anonymous queries only`,
};
export const AnonymousQueryOptions = AnonymousQueryOptionValues.map(
  (value) => ({
    value,
    label: AnonymousQueryOptionLabels[value],
  }),
);
// #endregion

// #region Device
export const Devices = ["DESKTOP", "MOBILE", "TABLET"] as const;
export type Device = (typeof Devices)[number];
export const DeviceLabels: Record<Device, ReturnType<typeof msg>> = {
  DESKTOP: msg`Desktop`,
  MOBILE: msg`Mobile`,
  TABLET: msg`Tablet`,
} as const;
export const DeviceOptions = Devices.map((device) => ({
  value: device,
  label: DeviceLabels[device],
}));
// #endregion

// #region Country
const Countries = [
  "aia",
  "ala",
  "alb",
  "and",
  "are",
  "arg",
  "arm",
  "asm",
  "ata",
  "atf",
  "atg",
  "aus",
  "aut",
  "aze",
  "bdi",
  "bel",
  "ben",
  "bes",
  "bfa",
  "bgd",
  "bgr",
  "bhr",
  "bhs",
  "bih",
  "blm",
  "blr",
  "blz",
  "bmu",
  "bol",
  "bra",
  "brb",
  "brn",
  "btn",
  "bvt",
  "bwa",
  "caf",
  "can",
  "cck",
  "che",
  "chl",
  "chn",
  "civ",
  "cmr",
  "cod",
  "cog",
  "cok",
  "col",
  "com",
  "cpv",
  "cri",
  "cub",
  "cuw",
  "cxr",
  "cym",
  "cyp",
  "cze",
  "deu",
  "dji",
  "dma",
  "dnk",
  "dom",
  "dza",
  "ecu",
  "egy",
  "eri",
  "esh",
  "esp",
  "est",
  "eth",
  "fin",
  "fji",
  "flk",
  "fra",
  "fro",
  "fsm",
  "gab",
  "gbr",
  "geo",
  "ggy",
  "gha",
  "gib",
  "gin",
  "glp",
  "gmb",
  "gnb",
  "gnq",
  "grc",
  "grd",
  "grl",
  "gtm",
  "guf",
  "gum",
  "guy",
  "hkg",
  "hmd",
  "hnd",
  "hrv",
  "hti",
  "hun",
  "idn",
  "imn",
  "ind",
  "iot",
  "irl",
  "irn",
  "irq",
  "isl",
  "isr",
  "ita",
  "jam",
  "jey",
  "jor",
  "jpn",
  "kaz",
  "ken",
  "kgz",
  "khm",
  "kir",
  "kna",
  "kor",
  "kwt",
  "lao",
  "lbn",
  "lbr",
  "lby",
  "lca",
  "lie",
  "lka",
  "lso",
  "ltu",
  "lux",
  "lva",
  "mac",
  "maf",
  "mar",
  "mco",
  "mda",
  "mdg",
  "mdv",
  "mex",
  "mhl",
  "mkd",
  "mli",
  "mlt",
  "mmr",
  "mne",
  "mng",
  "mnp",
  "moz",
  "mrt",
  "msr",
  "mtq",
  "mus",
  "mwi",
  "mys",
  "myt",
  "nam",
  "ncl",
  "ner",
  "nfk",
  "nga",
  "nic",
  "niu",
  "nld",
  "nor",
  "npl",
  "nru",
  "nzl",
  "omn",
  "pak",
  "pan",
  "pcn",
  "per",
  "phl",
  "plw",
  "png",
  "pol",
  "pri",
  "prk",
  "prt",
  "pry",
  "pse",
  "pyf",
  "qat",
  "reu",
  "rou",
  "rus",
  "rwa",
  "sau",
  "sdn",
  "sen",
  "sgp",
  "sgs",
  "shn",
  "sjm",
  "slb",
  "sle",
  "slv",
  "smr",
  "som",
  "spm",
  "srb",
  "ssd",
  "stp",
  "sur",
  "svk",
  "svn",
  "swe",
  "swz",
  "sxm",
  "syc",
  "syr",
  "tca",
  "tcd",
  "tgo",
  "tha",
  "tjk",
  "tkl",
  "tkm",
  "tls",
  "ton",
  "tto",
  "tun",
  "tur",
  "tuv",
  "twn",
  "tza",
  "uga",
  "ukr",
  "umi",
  "ury",
  "usa",
  "uzb",
  "vat",
  "vct",
  "ven",
  "vgb",
  "vir",
  "vnm",
  "vut",
  "wlf",
  "wsm",
  "yem",
  "zaf",
  "zmb",
  "zwe",
  "zzz",
] as const;
export type Country = (typeof Countries)[number];
// #endregion

// #region ManageType
export type SearchAnalyticsManageType = ReturnType<
  typeof graphql.scalar<"SearchAnalyticsManageType">
>;
export const ManageTypeLabels: Record<
  SearchAnalyticsManageType,
  ReturnType<typeof msg>
> = {
  JADE: msg`Managed by Amethyst`,
  SELF: msg`Self-managed`,
  API: msg`API-connected`,
} as const;
// #endregion
